import React, { useEffect, useState } from "react";
import "../styles/layout.css";
import ReusableHeader from "../components/ReusableHeader";
import CustomButton from "../components/CustomButton";
import CustomDropdown from "../components/CustomDropdown";
import ReusableFilter from "../components/ReusableFilter";
import { useNavigate } from "react-router-dom";
import { decodeJwt } from "../util/decodeJwt";
import DashboardWidget from "../components/DashboardWidget";
import { useOrderApi } from "../api/orders";
import { Filter } from "lucide-react";

export default function InventoryOverviewDashbboard() {
  const { getDashboard } = useOrderApi();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Filters ---
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0]; // "YYYY-MM-DD"

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState(currentDate);
  const [toDate, setToDate] = useState(currentDate);
  const [selectedModel, setSelectedModel] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [filterObject, setFilterObject] = useState({});

  // Decode user info from token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setUserId(decoded?._id || null);
      setUserRole(decoded?.role || "");
    }
  }, []);

  // Fetch dashboard with optional filters
  const fetchDashboard = async ({ fromDate, toDate } = {}) => {
    try {
      setLoading(true);
      const response = await getDashboard({ fromDate, toDate });
      setDashboardData(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (!userId) return;
    fetchDashboard({ fromDate, toDate });
  }, [userId]);

  // Apply filters
  const handleApplyFilters = () => {
    setFilterObject({
      fromDate,
      toDate,
      deviceModelIds: selectedModel,
    });
    setIsFilterOpen(false);
    fetchDashboard({
      fromDate,
      toDate,
    });
  };

  const renderActionButtons = () => (
    <div className="header-buttons">
      {/* Applied Filter Chips */}
      {(filterObject.fromDate || filterObject.toDate) && (
        <div className="filter-chips-inline">
          <span className="filter-chip">
            From: {filterObject.fromDate || currentDate}
          </span>
          <span className="filter-chip">
            To: {filterObject.toDate || currentDate}
          </span>
          <button
            className="clear-chip-btn"
            onClick={() => {
              setFromDate(currentDate);
              setToDate(currentDate);
              setFilterObject({});
              fetchDashboard({ fromDate: currentDate, toDate: currentDate });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {userRole === "manufacturer" && (
        <CustomButton
          className="header-btn"
          label="New Inward"
          onClick={() => navigate("/inventory-management/inward")}
        />
      )}
      <CustomButton
        className="header-btn"
        label="New Outward"
        onClick={() => navigate("/inventory-management/outward")}
      />
      <CustomButton
        className="header-btn"
        label={<Filter size={16} className="inline-block mr-1" />}
        onClick={() => setIsFilterOpen(true)}
      />
    </div>
  );

  return (
    <div className="inventoryManagement">
      <ReusableHeader title="Inventory Overview" children={renderActionButtons()} />

      {/* Loading & error */}
      {loading && <p>Loading dashboard...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Dashboard widget */}
      {dashboardData && <DashboardWidget data={dashboardData} />}

      {/* Reusable Filter */}
      <ReusableFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Devices"
      >
        <div className="filter-fields">
          {/* From Date */}
          <div className="filter-field">
            <label className="filter-label">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="date-input fancy-date"
            />
          </div>

          {/* To Date */}
          <div className="filter-field">
            <label className="filter-label">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="date-input fancy-date"
            />
          </div>

          <div className="filter-actions">
            <CustomButton
              className="apply-btn"
              label="Apply"
              onClick={handleApplyFilters}
            />
            <CustomButton
              className="clear-btn"
              label="Clear"
              onClick={() => {
                setSelectedModel([]);
                setFromDate(currentDate);
                setToDate(currentDate);
                setFilterObject({});
                setIsFilterOpen(false);
                fetchDashboard({ fromDate: currentDate, toDate: currentDate });
              }}
            />
          </div>
        </div>
      </ReusableFilter>
    </div>
  );
}
